/**
 * Formatação de número, data, hora, unidade e tempo relativo.
 *
 * Duas regras que valem para o app inteiro:
 *
 * 1. Nada de formatação manual. Sem `toFixed` seguido de `replace(".", ",")`,
 *    sem montar "1.640" na mão. Tudo passa por Intl com o idioma ativo.
 *
 * 2. O fuso de exibição é o mesmo da regra de negócio: America/Sao_Paulo.
 *    A ofensiva vira à meia-noite de São Paulo (src/utils/streakStatus.js);
 *    se a tela formatasse no fuso do aparelho, alguém viajando veria "ontem"
 *    num registro que o servidor considera de hoje. Traduzir muda o idioma,
 *    não o calendário.
 */

import i18n from './index';

export const FUSO_NEGOCIO = 'America/Sao_Paulo';

const idioma = () => i18n.language || 'pt-BR';

// Intl é caro de instanciar; um cache por (idioma + opções) resolve.
const cache = new Map();
function memo(chave, criar) {
  const k = `${idioma()}|${chave}`;
  if (!cache.has(k)) cache.set(k, criar());
  return cache.get(k);
}

// troca de idioma invalida o cache
i18n.on('languageChanged', () => cache.clear());

// ── Número ──────────────────────────────────────────────────

/**
 * @param {number} valor
 * @param {{min?: number, max?: number, sinal?: boolean}} [opcoes] casas decimais;
 *   `sinal` mostra o + do ganho e o - do gasto (delta de moeda e de XP),
 *   pelo Intl e não por concatenação, para o - vir certo em cada idioma.
 */
export function formatarNumero(valor, opcoes = {}) {
  if (valor == null || Number.isNaN(Number(valor))) return '';
  const { min = 0, max = 0, sinal = false, agrupar } = opcoes;

  // MILHAR DE QUATRO DÍGITOS SAI SEM SEPARADOR: 1500, não 1.500.
  //
  // Os números do app são quase todos de quatro dígitos (XP, pontos de
  // competição, kcal da semana) e o ponto do pt-BR ali só rouba largura numa
  // caixa apertada — e, em fonte tabular, ainda desalinha a coluna. De cinco
  // dígitos em diante o agrupamento volta, porque aí ele passa a ajudar a
  // ler. Quem precisar do contrário passa `agrupar` explicitamente.
  const n = Number(valor);
  const agrupamento = agrupar ?? Math.abs(n) >= 10000;

  const fmt = memo(`n:${min}:${max}:${sinal ? 's' : ''}:${agrupamento ? 'g' : ''}`, () =>
    new Intl.NumberFormat(idioma(), {
      minimumFractionDigits: min,
      maximumFractionDigits: max,
      useGrouping: agrupamento,
      ...(sinal ? { signDisplay: 'exceptZero' } : null),
    })
  );
  return fmt.format(n);
}

/** Percentual. `formatarPercentual(0.64)` -> "64%" */
export function formatarPercentual(fracao, casas = 0) {
  if (fracao == null || Number.isNaN(Number(fracao))) return '';
  const fmt = memo(`p:${casas}`, () =>
    new Intl.NumberFormat(idioma(), {
      style: 'percent',
      minimumFractionDigits: casas,
      maximumFractionDigits: casas,
    })
  );
  return fmt.format(Number(fracao));
}

// ── Unidade ─────────────────────────────────────────────────

/**
 * Símbolos que não se traduzem. Ficam aqui, e não num JSON de tradução,
 * porque são notação científica: "kg" é kg em português e em inglês.
 * Unidades que viram PALAVRA (passos, dias) não entram aqui — essas são
 * chave de tradução com plural.
 */
const SIMBOLOS = {
  kcal: 'kcal',
  g: 'g',
  kg: 'kg',
  km: 'km',
  m: 'm',
  cm: 'cm',
  min: 'min',
  h: 'h',
  s: 's',
  t: 't',
  xp: 'XP',
};

/** Casas decimais razoáveis por unidade, quando não vier explícito. */
const CASAS_PADRAO = { kg: 1, km: 2, t: 1, cm: 1, g: 0, kcal: 0, min: 0, m: 0, h: 0, s: 0, xp: 0 };

/**
 * `formatarUnidade(1640, 'kcal')` -> "1.640 kcal" (pt) / "1,640 kcal" (en)
 * `formatarUnidade(0.37, 'kg')`   -> "0,37 kg"    / "0.37 kg"
 *
 * O espaço é fino e inquebrável (U+202F): o número nunca se separa da
 * unidade na quebra de linha.
 */
export function formatarUnidade(valor, unidade, opcoes = {}) {
  const simbolo = SIMBOLOS[unidade];
  if (!simbolo) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(`[formatos] unidade sem símbolo: "${unidade}". Se vira palavra, use chave de tradução com plural.`);
    }
    return formatarNumero(valor, opcoes);
  }
  const casas = opcoes.max ?? CASAS_PADRAO[unidade] ?? 0;
  const numero = formatarNumero(valor, { min: opcoes.min ?? 0, max: casas });
  return `${numero} ${simbolo}`;
}

/**
 * Só o símbolo da unidade. Serve para quando o número e a unidade têm
 * tamanhos diferentes na tela (o "kcal" 12/600 ao lado do "720" 24/800 da
 * Dieta): o componente monta os dois pedaços, mas o símbolo continua vindo
 * daqui, e não escrito à mão no JSX.
 */
export function simboloDaUnidade(unidade) {
  return SIMBOLOS[unidade] ?? '';
}

/** Alias curto usado nas telas. */
export const formatUnit = formatarUnidade;

// ── Data e hora ─────────────────────────────────────────────

const PRESETS = {
  curta: { day: '2-digit', month: '2-digit' },
  media: { day: '2-digit', month: 'short' },
  longa: { day: '2-digit', month: 'long', year: 'numeric' },
  mesAno: { month: 'long', year: 'numeric' },
  diaSemana: { weekday: 'short' },
  hora: { hour: '2-digit', minute: '2-digit' },
  dataHora: { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' },
};

/**
 * @param {Date|number|string} data
 * @param {keyof PRESETS|Intl.DateTimeFormatOptions} [formato]
 */
export function formatarData(data, formato = 'media') {
  const d = data instanceof Date ? data : new Date(data);
  if (Number.isNaN(d.getTime())) return '';
  const opcoes = typeof formato === 'string' ? PRESETS[formato] || PRESETS.media : formato;
  const chave = typeof formato === 'string' ? `d:${formato}` : `d:${JSON.stringify(formato)}`;
  const fmt = memo(chave, () =>
    new Intl.DateTimeFormat(idioma(), { timeZone: FUSO_NEGOCIO, ...opcoes })
  );
  return fmt.format(d);
}

export const formatDate = formatarData;

export function formatarHora(data) {
  return formatarData(data, 'hora');
}

// ── Tempo relativo ──────────────────────────────────────────

const DIVISORES = [
  ['second', 60],
  ['minute', 60],
  ['hour', 24],
  ['day', 7],
  ['week', 4.34524],
  ['month', 12],
  ['year', Infinity],
];

/**
 * "há 2 h", "2 h ago", "há 6 dias".
 *
 * @param {Date|number|string} data
 * @param {{estilo?: 'long'|'short'|'narrow', agora?: Date}} [opcoes]
 */
export function formatarRelativo(data, opcoes = {}) {
  const d = data instanceof Date ? data : new Date(data);
  if (Number.isNaN(d.getTime())) return '';

  const agora = opcoes.agora instanceof Date ? opcoes.agora : new Date();
  const estilo = opcoes.estilo || 'long';

  const fmt = memo(`r:${estilo}`, () =>
    new Intl.RelativeTimeFormat(idioma(), { numeric: 'auto', style: estilo })
  );

  let delta = (d.getTime() - agora.getTime()) / 1000;
  for (const [unidade, divisor] of DIVISORES) {
    if (Math.abs(delta) < divisor) {
      return fmt.format(Math.round(delta), unidade);
    }
    delta /= divisor;
  }
  return formatarData(d, 'longa');
}

export const formatRelative = formatarRelativo;

// ── Lista ───────────────────────────────────────────────────

/**
 * "Treino e Dieta" / "Workout and Diet".
 *
 * Existe porque hoje há `join(' e ')` no código: em inglês sairia
 * "Diet e Body". Conjunção é gramática, não texto solto.
 */
export function formatarLista(itens, tipo = 'conjunction') {
  const lista = (itens || []).filter(Boolean).map(String);
  if (!lista.length) return '';
  try {
    const fmt = memo(`l:${tipo}`, () =>
      new Intl.ListFormat(idioma(), { style: 'long', type: tipo })
    );
    return fmt.format(lista);
  } catch {
    return lista.join(', '); // ambiente sem Intl.ListFormat
  }
}

export const formatList = formatarLista;
